import c from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setModalOpened } from 'store/modal';

import { Button, ButtonColors, TextColor, Typography } from '@repo/ui';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useIsFirstModalView } from 'hooks/useIsFirstModalView';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';

import bolts from 'assets/images/summary/first-look/conditions_bolts.svg';
import boltsBaby from 'assets/images/summary/first-look/conditions_bolts_baby.svg';
import illustration from 'assets/images/summary/first-look/conditions_illustration.svg';
import illustrationBaby from 'assets/images/summary/first-look/conditions_illustration_baby.svg';

import { KitType } from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import styles from './Conditions.module.css';
import slideStyles from './Slides.module.css';

function getVariant(
  isVaginal: boolean,
  ageGroup: AgeGroup,
): {
  characterImage: string;
  characterClass: string;
  boltsImage: string;
  boltsClass: string;
  backgroundStyle: string;
} {
  if (isVaginal) {
    return {
      characterImage: '',
      characterClass: '',
      boltsImage: '',
      boltsClass: '',
      backgroundStyle: styles.bottomBackgroundVaginal,
    };
  }

  switch (ageGroup) {
    case AgeGroup.BABY:
      return {
        characterImage: illustrationBaby,
        characterClass: styles.illustrationBaby,
        boltsImage: boltsBaby,
        boltsClass: styles.boltsBaby,
        backgroundStyle: styles.bottomBackground,
      };
    default:
      return {
        characterImage: illustration,
        characterClass: styles.illustration,
        boltsImage: bolts,
        boltsClass: styles.bolts,
        backgroundStyle: styles.bottomBackground,
      };
  }
}

export const Conditions: React.FC = () => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const navigate = useNavigate();

  const isFirstView = useIsFirstModalView('firstLook');

  const modalIdentifier = {
    name: 'firstLook',
    props: {},
  };

  const dismissModal = () => {
    dispatch(setModalOpened(modalIdentifier));
  };

  const handleViewConditions = () => {
    trackEvent('First Look', {
      event_type: 'button_clicked',
      button_text: 'View associated conditions',
      slide_id: 'conditions',
      action: 'view_conditions',
    });
    dismissModal();
    if (currentKitId) {
      navigate(`/results/${currentKitId}/conditions/metrics`);
    }
  };

  const ageGroup = getAgeGroup(currentKit?.age_at_sampling ?? 0);
  const isAdult = ageGroup === AgeGroup.ADULT;

  const variant = getVariant(
    currentKit?.sampling_type === KitType.VAGINAL,
    ageGroup,
  );

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slideContainer)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.conditions }}
    >
      <div className={styles.contentContainer}>
        <Typography variant='body-m'>Did you know?</Typography>
        <Typography
          variant='heading-2xl'
          color={TextColor.DARKORANGE}
          weight='medium'
        >
          Microbes and their functions are associated with several health
          conditions.
        </Typography>
        <Typography variant='body-m'>
          View {isAdult ? 'your' : 'the'} conditions page to learn more.
        </Typography>
      </div>
      <div className={c(styles.bottom, variant.backgroundStyle)}>
        <img src={variant.characterImage} className={variant.characterClass} />
        <img src={variant.boltsImage} className={variant.boltsClass} />

        {!isFirstView && (
          <div className={styles.buttonsContainer}>
            <Button
              variant='outline'
              color={ButtonColors.PURPLE}
              onClick={handleViewConditions}
            >
              View associated conditions
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
